import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader, Image, RefreshCw, Building2, FolderSync as Sync } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { verifyAndFixRestaurantPictures, fixRestaurantPictures, syncRestaurantPictures, syncAllRestaurantPictures } from '../lib/storage';
import BackButton from '../components/BackButton';

const FixRestaurantPictures = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);
  const [fixingRestaurantId, setFixingRestaurantId] = useState<string | null>(null);
  const [syncingRestaurantId, setSyncingRestaurantId] = useState<string | null>(null);
  const [fixResults, setFixResults] = useState<{[key: string]: {success: boolean, message?: string, error?: string}}>({});
  const [syncResults, setSyncResults] = useState<{[key: string]: {success: boolean, message?: string, error?: string}}>({});

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) {
        navigate('/login');
        return;
      }

      // Fetch restaurants
      const { data: restaurants, error: fetchError } = await supabase
        .from('restaurants')
        .select('id, name, pictures, created_at')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      
      console.log('Fetched restaurants:', restaurants);
      setRestaurants(restaurants || []);
    } catch (err) {
      console.error('Error fetching restaurants:', err);
      setError(err instanceof Error ? err.message : 'Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAll = async () => {
    try {
      setVerifying(true);
      setError(null);
      setSuccess(null);
      
      const result = await verifyAndFixRestaurantPictures();
      
      if (result.success) {
        setSuccess('Verification and fixes completed successfully');
        // Refresh the restaurant list
        await fetchRestaurants();
      } else {
        setError(result.error || 'Verification failed');
      }
    } catch (err) {
      console.error('Error verifying restaurants:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify restaurants');
    } finally {
      setVerifying(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      
      const result = await syncAllRestaurantPictures();
      
      if (result.success) {
        setSuccess('Synchronization completed successfully');
        // Refresh the restaurant list
        await fetchRestaurants();
      } else {
        setError(result.error || 'Synchronization failed');
      }
    } catch (err) {
      console.error('Error syncing restaurants:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync restaurants');
    } finally {
      setSyncing(false);
    }
  };

  const handleFixRestaurant = async (restaurantId: string) => {
    try {
      setFixingRestaurantId(restaurantId);
      setError(null);
      
      const result = await fixRestaurantPictures(restaurantId);
      
      setFixResults(prev => ({
        ...prev,
        [restaurantId]: result
      }));
      
      // Refresh the restaurant list
      await fetchRestaurants();
    } catch (err) {
      console.error(`Error fixing restaurant ${restaurantId}:`, err);
      setFixResults(prev => ({
        ...prev,
        [restaurantId]: { 
          success: false, 
          error: err instanceof Error ? err.message : 'Failed to fix restaurant' 
        }
      }));
    } finally {
      setFixingRestaurantId(null);
    }
  };

  const handleSyncRestaurant = async (restaurantId: string) => {
    try {
      setSyncingRestaurantId(restaurantId);
      setError(null);
      
      const result = await syncRestaurantPictures(restaurantId);
      
      setSyncResults(prev => ({
        ...prev,
        [restaurantId]: result
      }));
      
      // Refresh the restaurant list
      await fetchRestaurants();
    } catch (err) {
      console.error(`Error syncing restaurant ${restaurantId}:`, err);
      setSyncResults(prev => ({
        ...prev,
        [restaurantId]: { 
          success: false, 
          error: err instanceof Error ? err.message : 'Failed to sync restaurant' 
        }
      }));
    } finally {
      setSyncingRestaurantId(null);
    }
  };

  const hasPicturesStructure = (restaurant: any) => {
    return restaurant.pictures && 
           typeof restaurant.pictures === 'object' &&
           restaurant.pictures.gallery !== undefined &&
           restaurant.pictures.menu !== undefined &&
           restaurant.pictures.interior !== undefined &&
           restaurant.pictures.exterior !== undefined;
  };

  const countPictures = (restaurant: any) => {
    if (!hasPicturesStructure(restaurant)) return 0;
    
    return (
      (Array.isArray(restaurant.pictures.gallery) ? restaurant.pictures.gallery.length : 0) +
      (Array.isArray(restaurant.pictures.menu) ? restaurant.pictures.menu.length : 0) +
      (Array.isArray(restaurant.pictures.interior) ? restaurant.pictures.interior.length : 0) +
      (Array.isArray(restaurant.pictures.exterior) ? restaurant.pictures.exterior.length : 0)
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center space-x-4 mb-8">
        <BackButton />
        <h1 className="text-3xl font-bold text-gray-900">Fix Restaurant Pictures</h1>
      </div>

      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          <span>{success}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Restaurant Pictures Verification</h2>
          <div className="flex space-x-4">
            <button
              onClick={handleVerifyAll}
              disabled={verifying}
              className={`flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition ${
                verifying ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {verifying ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-5 w-5" />
                  <span>Verify & Fix All</span>
                </>
              )}
            </button>
            <button
              onClick={handleSyncAll}
              disabled={syncing}
              className={`flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition ${
                syncing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {syncing ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  <span>Syncing...</span>
                </>
              ) : (
                <>
                  <Sync className="h-5 w-5" />
                  <span>Sync All with Storage</span>
                </>
              )}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Restaurant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pictures Structure
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Picture Count
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {restaurants.map((restaurant) => (
                <tr key={restaurant.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{restaurant.name}</div>
                        <div className="text-sm text-gray-500">{restaurant.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      hasPicturesStructure(restaurant) 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {hasPicturesStructure(restaurant) ? 'Valid' : 'Invalid'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {countPictures(restaurant)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(restaurant.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleFixRestaurant(restaurant.id)}
                        disabled={fixingRestaurantId === restaurant.id}
                        className={`text-blue-600 hover:text-blue-900 ${
                          fixingRestaurantId === restaurant.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {fixingRestaurantId === restaurant.id ? (
                          <span className="flex items-center">
                            <Loader className="h-4 w-4 animate-spin mr-1" />
                            Fixing...
                          </span>
                        ) : (
                          'Fix Pictures'
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleSyncRestaurant(restaurant.id)}
                        disabled={syncingRestaurantId === restaurant.id}
                        className={`text-green-600 hover:text-green-900 ${
                          syncingRestaurantId === restaurant.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {syncingRestaurantId === restaurant.id ? (
                          <span className="flex items-center">
                            <Loader className="h-4 w-4 animate-spin mr-1" />
                            Syncing...
                          </span>
                        ) : (
                          'Sync with Storage'
                        )}
                      </button>
                    </div>
                    
                    {fixResults[restaurant.id] && (
                      <span className={`ml-2 text-xs ${
                        fixResults[restaurant.id].success ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {fixResults[restaurant.id].success 
                          ? fixResults[restaurant.id].message || 'Fixed' 
                          : fixResults[restaurant.id].error || 'Failed'}
                      </span>
                    )}
                    
                    {syncResults[restaurant.id] && (
                      <span className={`ml-2 text-xs ${
                        syncResults[restaurant.id].success ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {syncResults[restaurant.id].success 
                          ? syncResults[restaurant.id].message || 'Synced' 
                          : syncResults[restaurant.id].error || 'Failed'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              
              {restaurants.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No restaurants found
                  </td>
                </tr>
              )}
              
              {loading && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <Loader className="h-6 w-6 animate-spin text-blue-500 mx-auto" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Restaurant Details */}
      {selectedRestaurantId && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Restaurant Details</h2>
          {/* Restaurant details content */}
        </div>
      )}
    </div>
  );
};

export default FixRestaurantPictures;